<template>
  <div class="user-history-page">
    <h1 class="page-title">История изменений пользователей</h1>
    
    <!-- Форма фильтров -->
    <div class="filters-section">
      <div class="filter-card">
        <div class="filter-card__header">
          <span>Фильтры</span>
          <button 
            @click="exportHistory"
            :disabled="tableData.length === 0 || exportLoading"
            class="button button--export"
            title="Экспорт истории"
          >
            <svg class="export-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
            </svg>
            {{ exportLoading ? 'Экспорт...' : 'Экспорт в Excel' }}
          </button>
        </div>
        
        <div class="filters filters--indent filters--between">
          <div class="filters__block">
            <!-- Год -->
            <div class="filters__item">
              <label class="filters__label">Год</label>
              <select v-model="selectedYear" @change="applyFilters" class="select">
                <option value="">Все годы</option>
                <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            
            <!-- Месяц -->
            <div class="filters__item">
              <label class="filters__label">Месяц</label>
              <select v-model="selectedMonth" @change="applyFilters" class="select">
                <option value="">Все месяцы</option>
                <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
              </select>
            </div>
            
            <!-- Тип изменения -->
            <div class="filters__item">
              <label class="filters__label">Тип</label>
              <select v-model="selectedChangeType" @change="applyFilters" class="select">
                <option value="">Все</option>
                <option value="+">Создание</option>
                <option value="~">Изменение</option>
                <option value="-">Удаление</option>
              </select>
            </div>
            
            <!-- Роль пользователя -->
            <div class="filters__item">
              <label class="filters__label">Роль</label>
              <select v-model="selectedUserRole" @change="applyFilters" class="select">
                <option value="">Все роли</option>
                <option v-for="role in userRoleOptions" :key="role.value" :value="role.value">{{ role.label }}</option>
              </select>
            </div>
            
            <!-- Группа пользователя -->
            <div class="filters__item">
              <label class="filters__label">Группа</label>
              <select v-model="selectedUserGroup" @change="applyFilters" class="select">
                <option value="">Все группы</option>
                <option v-for="group in userGroupOptions" :key="group.value" :value="group.value">{{ group.label }}</option>
              </select>
            </div>
            
            <!-- Региональная организация -->
            <div class="filters__item">
              <label class="filters__label">Регион</label>
              <select v-model="selectedRegionOrg" @change="applyFilters" class="select">
                <option value="">Все регионы</option>
                <option v-for="org in regionOrgOptions" :key="org.id" :value="org.id">{{ org.name }}</option>
              </select>
            </div>
            
            <!-- Раздел -->
            <div class="filters__item">
              <label class="filters__label">Раздел</label>
              <select v-model="selectedSection" @change="applyFilters" class="select">
                <option value="">Все разделы</option>
                <option v-for="opt in sectionOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
          </div>
          
          <div class="filters__block filters__block--wide">
            <!-- Пользователь -->
            <div class="filters__item filters__item--wide">
              <label class="filters__label">Пользователь</label>
              <select v-model="selectedUser" @change="applyFilters" class="select">
                <option value="">Все пользователи</option>
                <option v-for="user in userOptions" :key="user.id" :value="user.id">
                  {{ user.full_name }} ({{ user.username }})
                </option>
              </select>
            </div>
          </div>
          
          <div class="filters__block filters__block--actions">
            <button @click="applyFilters" type="button" class="button">Применить</button>
            <button @click="resetFilters" type="button" class="button button--border">Сброс</button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Таблица -->
    <div class="table-section">
      <v-client-table
        :data="tableData"
        :columns="tableColumns"
        :options="tableOptions"
        ref="historyTable"
        class="table-default table-default--dynamic"
      >
        <div slot="timestamp" slot-scope="props">{{ formatDate(props.row.timestamp) }}</div>
        <div slot="user_name" slot-scope="props">{{ props.row.user_name || 'Система' }}</div>
        <div slot="region_name" slot-scope="props">{{ props.row.region_name || '—' }}</div>
        <div slot="changes" slot-scope="props">
          <div><strong>{{ props.row.object_repr || '—' }}</strong></div>
          
          <div v-if="props.row.action_type === '+'">{{ getNewObjectMessage(props.row.model_name) }}</div>
          <div v-else-if="props.row.action_type === '-'">{{ getDeletedObjectMessage(props.row.model_name) }}</div>
          <div v-else>
            <ul class="changes-list" v-if="props.row.changes && Object.keys(props.row.changes).length">
              <li v-for="(change, field) in props.row.changes" :key="field">
                <strong>{{ getFieldLabel(field) }}:</strong>
                <template v-if="field === 'organization' || field === 'region_org'">
                  <span class="old-value">{{ formatOrganizationValue(change.old) }}</span> →
                  <span class="new-value">{{ formatOrganizationValue(change.new) }}</span>
                </template>
                <template v-else>
                  <span class="old-value">{{ formatSimpleValue(change.old) }}</span> →
                  <span class="new-value">{{ formatSimpleValue(change.new) }}</span>
                </template>
              </li>
            </ul>
            <div v-else class="no-changes">Нет изменений в отслеживаемых полях</div>
          </div>
        </div>
        <div slot="actions" slot-scope="props">
          <a v-if="getObjectUrl(props.row.model_name, props.row.object_id)" :href="getObjectUrl(props.row.model_name, props.row.object_id)" target="_blank" class="action-link">Перейти</a>
          <span v-else>—</span>
        </div>
      </v-client-table>
      
      <paginate
        v-if="pagination.pages > 1"
        v-model="pagination.page"
        :page-count="pagination.pages"
        :clickHandler="clickPaginationCallback"
        :prev-text="'<'"
        :next-text="'>'"
        :container-class="'pagination'"
        :page-class="'pagination__item'"
        :page-link-class="'pagination__link'"
        :prev-class="'pagination__prev'"
        :next-class="'pagination__next'"
        :next-link-class="'pagination__link-next'"
        :prev-link-class="'pagination__link-prev'"
      ></paginate>
    </div>
  </div>
</template>

<script>
import axios from '@/http/axios/axios';

export default {
  name: 'UserHistory',
  
  data() {
    return {
      tableData: [],
      tableColumns: ['timestamp', 'user_name', 'region_name', 'changes', 'actions'],
      tableOptions: {
        headings: {
          timestamp: 'Дата и время',
          user_name: 'Кто изменил',
          region_name: 'Регион',
          changes: 'Изменения',
          actions: 'Действия'
        },
        pagination: { show: false },
        filterable: false,
        sortable: ['timestamp', 'user_name', 'region_name'],
        perPage: 20,
        texts: {
          filter: '',
          filterPlaceholder: 'Поиск...',
          noResults: 'История изменений не найдена',
          loading: 'Загрузка...',
          count: '',
          limit: 'Записей:',
          page: 'Страница:'
        },
      },
      pagination: { pages: 1, page: 1, count: 0 },
      
      // Фильтры
      filters: {
        year: '',
        month: '',
        action_type: '',
        user_id: '',
        role: '',
        group_type: '',
        region_org_id: '',
        section: '',
      },
      
      // Выбранные значения
      selectedYear: '',
      selectedMonth: '',
      selectedChangeType: '',
      selectedUserRole: '',
      selectedUser: '',
      selectedUserGroup: '',
      selectedRegionOrg: '',
      selectedSection: '',
      
      // Опции
      userOptions: [],
      allYearOptions: [{ value: '', label: 'Все годы' }],
      allMonthOptions: [
        { value: '', label: 'Все месяцы' },
        { value: '1', label: 'Январь' }, { value: '2', label: 'Февраль' }, { value: '3', label: 'Март' },
        { value: '4', label: 'Апрель' }, { value: '5', label: 'Май' }, { value: '6', label: 'Июнь' },
        { value: '7', label: 'Июль' }, { value: '8', label: 'Август' }, { value: '9', label: 'Сентябрь' },
        { value: '10', label: 'Октябрь' }, { value: '11', label: 'Ноябрь' }, { value: '12', label: 'Декабрь' },
      ],
      changeTypeOptions: [
        { value: '', label: 'Все типы' },
        { value: '+', label: 'Создание' },
        { value: '~', label: 'Изменение' },
        { value: '-', label: 'Удаление' },
      ],
      userRoleOptions: [
        { value: '', label: 'Все роли' },
        { value: 'admin', label: 'Администратор' },
        { value: 'staff', label: 'Персонал' },
        { value: 'user', label: 'Пользователь' },
      ],
      userGroupOptions: [
        { value: '', label: 'Все группы' },
        { value: 'region', label: 'Региональная' },
        { value: 'local', label: 'Местная' },
        { value: 'not_admin', label: 'Все кроме администраторов' },
      ],
      regionOrgOptions: [{ value: '', label: 'Все регионы' }],
      
      // Рабочие списки
      yearOptions: [{ value: '', label: 'Все годы' }],
      monthOptions: [
        { value: '', label: 'Все месяцы' },
        { value: '1', label: 'Январь' }, { value: '2', label: 'Февраль' }, { value: '3', label: 'Март' },
        { value: '4', label: 'Апрель' }, { value: '5', label: 'Май' }, { value: '6', label: 'Июнь' },
        { value: '7', label: 'Июль' }, { value: '8', label: 'Август' }, { value: '9', label: 'Сентябрь' },
        { value: '10', label: 'Октябрь' }, { value: '11', label: 'Ноябрь' }, { value: '12', label: 'Декабрь' },
      ],
      
      // Опции разделов
      sectionOptions: [
        { value: '', label: 'Все разделы' },
        { value: 'anketa_rukovoditelya', label: 'Анкета руководителя' },
        { value: 'pasport_ro', label: 'Паспорт РО РСМ' },
        { value: 'mestnye_organizacii', label: 'Местные организации' },
        { value: 'pervichnye_gruppy', label: 'Первичные группы' },
        { value: 'zasedaniya_ro', label: 'Заседания руководящих органов' },
        { value: 'otchetnost_minjust', label: 'Отчетность в Минюст' },
        { value: 'buhgalterskij_balans', label: 'Бухгалтерский баланс' },
        { value: 'uvedomlenie_programmy', label: 'Уведомление о программах' },
        { value: 'informacionnoe_obespechenie', label: 'Информационное обеспечение' },
        { value: 'soglasheniya_partner', label: 'Соглашения с партнерами' },
        { value: 'granty_subsidii', label: 'Гранты и субсидии' },
        { value: 'kadrovye_shkoly', label: 'Кадровые школы' },
        { value: 'plan_meropriyatiy', label: 'План мероприятий РО РСМ' },
        { value: 'vzaimodejstvie_sreda', label: 'Взаимодействие с внешней средой' },
      ],
      
      // Карта организаций для отображения
      organizationMap: {},
      
      // Экспорт
      exportLoading: false,
    };
  },

  async created() {
    console.log("Компонент UserHistory создан");
    this.generateFullYearOptions();
    await this.loadOrganizationsFromStore();
    await this.loadUsers();
    await this.loadHistory();
    this.filterAvailableOptions();
  },

  methods: {
    // === Загрузка пользователей ===
    async loadUsers() {
      try {
        const response = await axios.get('/user/', { params: { page_size: 500 } });
        if (response.data && Array.isArray(response.data.results)) {
          this.userOptions = response.data.results.map(user => ({
            id: user.id,
            username: user.username,
            full_name: `${user.last_name || ''} ${user.first_name || ''}`.trim() || user.username
          }));
          console.log("Загружено пользователей:", this.userOptions.length);
        }
      } catch (error) {
        console.error("Ошибка при загрузке пользователей:", error);
        this.$message?.error?.('Ошибка при загрузке списка пользователей');
      }
    },

    // === Экспорт истории ===
    async exportHistory() {
      if (this.exportLoading || this.tableData.length === 0) return;
      
      this.exportLoading = true;
      try {
        const params = { ...this.filters };
        // Удаляем пустые параметры
        Object.keys(params).forEach(key => {
          if (!params[key] && params[key] !== 0) delete params[key];
        });
        params.format = 'xlsx';
        params.export_all = 'true';
        
        const response = await axios.get('/user/users/unified-history/export/', {
          params,
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `history_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.$message?.success?.('Экспорт выполнен успешно');
      } catch (error) {
        console.error('Ошибка экспорта:', error);
        this.$message?.error?.('Ошибка экспорта');
      } finally {
        this.exportLoading = false;
      }
    },
    
    // === Генерация опций годов ===
    generateFullYearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [{ value: '', label: 'Все годы' }];
      for (let i = currentYear - 5; i <= currentYear + 1; i++) {
        years.push({ value: i.toString(), label: i.toString() });
      }
      this.allYearOptions = years;
      this.yearOptions = [...years];
    },

    // === Применение фильтров ===
    applyFilters() {
      // Валидация выбранных значений
      if (this.selectedYear && !this.yearOptions.some(opt => opt === this.selectedYear)) {
        this.selectedYear = '';
      }
      if (this.selectedMonth && !this.monthOptions.some(opt => opt.value === this.selectedMonth)) {
        this.selectedMonth = '';
      }

      // Сбор параметров фильтрации
      this.filters = {
        year: this.selectedYear || undefined,
        month: this.selectedMonth || undefined,
        action_type: this.selectedChangeType || undefined,
        user_id: this.selectedUser || undefined,
        role: this.selectedUserRole || undefined,
        group_type: this.selectedUserGroup || undefined,
        region_org_id: this.selectedRegionOrg || undefined,
        section: this.selectedSection || undefined,
      };

      this.pagination.page = 1;
      this.loadHistory().then(() => {
        this.filterAvailableOptions();
      });
    },

    // === Сброс фильтров ===
    resetFilters() {
      this.selectedYear = '';
      this.selectedMonth = '';
      this.selectedChangeType = '';
      this.selectedUserRole = '';
      this.selectedUserGroup = '';
      this.selectedRegionOrg = '';
      this.selectedUser = '';
      this.selectedSection = '';
      this.filters = {};
      
      this.yearOptions = [...this.allYearOptions];
      this.monthOptions = [...this.allMonthOptions];
      this.pagination.page = 1;
      
      this.loadHistory().then(() => {
        this.filterAvailableOptions();
      });
    },

    // === Загрузка регионов (с фолбэком) ===
    async loadOrganizationsFromStore() {
      try {
        // Пытаемся получить из Vuex
        const response = await this.$store.dispatch('regOrg/GET_QUERY');
        console.log("Ответ от regOrg/GET_QUERY:", response);

        let orgData = [];
        
        // Поддерживаем разные форматы ответа от API/Vuex
        if (response && response.data && Array.isArray(response.data)) {
          orgData = response.data;
        } else if (response && response.results && Array.isArray(response.results)) {
          orgData = response.results;
        } else if (Array.isArray(response)) {
          orgData = response;
        } else if (response && response.response && response.response.data && Array.isArray(response.response.data)) {
          orgData = response.response.data;
        }
        
        console.log("Данные организаций:", orgData);
        
        if (orgData && orgData.length > 0) {
          // Создаем маппинг для быстрого поиска по ID
          this.organizationMap = orgData.reduce((map, org) => {
            map[org.id] = org.name;
            return map;
          }, {});
          
          // Создаем опции для select (используем id как value, name как label)
          this.regionOrgOptions = [
            { id: '', name: 'Все регионы' },
            ...orgData.map(org => ({ id: org.id, name: org.name }))
          ];
          console.log("Опции регионов загружены:", this.regionOrgOptions.length);
        } else {
          console.warn("Получен пустой список организаций");
        }
      } catch (error) {
        console.warn("Vuex не вернул регионы, пробуем прямой запрос...", error);
        
        // Фолбэк: прямой запрос к API
        try {
          const fallbackResponse = await axios.get('/user/regions/');
          if (fallbackResponse.data && Array.isArray(fallbackResponse.data)) {
            this.organizationMap = fallbackResponse.data.reduce((map, org) => {
              map[org.id] = org.name;
              return map;
            }, {});
            this.regionOrgOptions = [
              { id: '', name: 'Все регионы' },
              ...fallbackResponse.data.map(org => ({ id: org.id, name: org.name }))
            ];
            console.log("Регионы загружены через фолбэк:", this.regionOrgOptions.length);
          }
        } catch (fallbackError) {
          console.error("Не удалось загрузить регионы:", fallbackError);
          this.$message?.warning?.('Не удалось загрузить список регионов');
        }
      }
    },

    // === Загрузка истории ===
    async loadHistory() {
      try {
        const params = {
          page: this.pagination.page,
          page_size: this.tableOptions.perPage,
          ordering: '-timestamp',
          ...this.filters
        };
        
        // Удаляем пустые параметры
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });

        const response = await axios.get('/user/users/unified-history/', { params });
        
        if (response.data && Array.isArray(response.data.results)) {
          this.tableData = response.data.results;
          this.pagination.pages = response.data.pages || 1;
          this.pagination.count = response.data.count || response.data.results.length;
          return Promise.resolve();
        } else {
          this.tableData = [];
          this.pagination.pages = 1;
          this.pagination.count = 0;
          return Promise.resolve();
        }
      } catch (error) {
        console.error('Ошибка при загрузке истории:', error);
        if (error.response) {
          if (error.response.status === 401 || error.response.status === 403) {
            this.$message?.error?.('Ошибка авторизации. Пожалуйста, войдите снова.');
          } else {
            this.$message?.error?.(`Ошибка: ${error.response.status}`);
          }
        } else {
          this.$message?.error?.('Ошибка сети или сервер не отвечает');
        }
        this.tableData = [];
        this.pagination.pages = 1;
        this.pagination.count = 0;
        return Promise.resolve();
      }
    },

    // === Пагинация ===
    clickPaginationCallback(page) {
      this.pagination.page = page;
      this.loadHistory().then(() => {
        this.filterAvailableOptions();
      });
    },

    // === Фильтрация доступных опций ===
    filterAvailableOptions() {
      const availableYears = new Set();
      const availableMonths = new Set();
      
      this.tableData.forEach(item => {
        if (item.timestamp) {
          const date = new Date(item.timestamp);
          if (!isNaN(date.getTime())) {
            availableYears.add(date.getFullYear());
            availableMonths.add(date.getMonth() + 1);
          }
        }
      });

      this.yearOptions = this.allYearOptions.filter(opt =>
        opt.value === '' || availableYears.has(parseInt(opt.value))
      );
      this.monthOptions = this.allMonthOptions.filter(opt =>
        opt.value === '' || availableMonths.has(parseInt(opt.value))
      );
    },

    // === Форматирование даты ===
    formatDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    },

    // === Сообщения для новых объектов ===
    getNewObjectMessage(modelName) {
      const labels = {
        'User': 'Новый пользователь',
        'FederalPublication': 'Новая федеральная публикация',
        'RegionPublication': 'Новая региональная публикация',
        'SimplePublication': 'Новая простая публикация',
        'PassportQuestionnaire': 'Новая анкета организации',
        'HeadQuestionnaire': 'Новая анкета руководителя',
        'AuditBody': 'Новый контрольно-ревизионный орган',
        'GoverningBody': 'Новый руководящий орган',
        'ManageBody': 'Новый орган управления',
        'ParticipantChamber': 'Новое участие в Общественной палате',
        'ParticipantCouncil': 'Новое участие в совете',
        'Partner': 'Новый партнер',
        'RealEstate': 'Новое недвижимое имущество',
        'SupportForm': 'Новая форма поддержки',
        'Moving': 'Новая заявка на перемещение',
        'Refuse': 'Новая заявка на снятие',
        'Continue': 'Новая заявка на продление',
        'LocalOrganization': 'Новая местная организация',
        'PrimaryGroup': 'Новая первичная группа',
        'Justice': 'Новая отчетность в Минюст',
        'MeetingGoverningBody': 'Новое заседание руководящих органов',
        'SupremeBody': 'Новое заседание высшего органа',
        'ActiveAgreement': 'Новое действующее соглашение',
        'Person': 'Новая кадровая школа',
        'EventPlan': 'Новое мероприятие',
        'BalanceSheet': 'Новый бухгалтерский баланс',
      };
      const label = labels[modelName] || `Новый ${modelName}`;
      return `${label} создан(а)`;
    },

    // === Сообщения для удалённых объектов ===
    getDeletedObjectMessage(modelName) {
      const labels = {
        'User': 'Пользователь',
        'FederalPublication': 'Федеральная публикация',
        'RegionPublication': 'Региональная публикация',
        'SimplePublication': 'Простая публикация',
        'PassportQuestionnaire': 'Анкета организации',
        'HeadQuestionnaire': 'Анкета руководителя',
        'AuditBody': 'Контрольно-ревизионный орган',
        'GoverningBody': 'Руководящий орган',
        'ManageBody': 'Орган управления',
        'ParticipantChamber': 'Участие в Общественной палате',
        'ParticipantCouncil': 'Участие в совете',
        'Partner': 'Партнер',
        'RealEstate': 'Недвижимое имущество',
        'SupportForm': 'Форма поддержки',
        'Moving': 'Заявка на перемещение',
        'Refuse': 'Заявка на снятие',
        'Continue': 'Заявка на продление',
        'LocalOrganization': 'Местная организация',
        'PrimaryGroup': 'Первичная группа',
        'Justice': 'Отчетность в Минюст',
        'MeetingGoverningBody': 'Заседание руководящих органов',
        'SupremeBody': 'Заседание высшего органа',
        'ActiveAgreement': 'Действующее соглашение',
        'Person': 'Кадровая школа',
        'EventPlan': 'Мероприятие',
        'BalanceSheet': 'Бухгалтерский баланс',
      };
      const label = labels[modelName] || modelName;
      return `${label} удалён(а)`;
    },

    // === Форматирование простых значений ===
    formatSimpleValue(value) {
      if (value === null || value === undefined) return '—';
      if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
      if (typeof value === 'object' && value !== null) {
        return value.common_name || value.name || value.id || String(value);
      }
      return String(value);
    },

    // === Форматирование значений организаций ===
    formatOrganizationValue(value) {
      if (value === null || value === undefined) return '—';
      if (typeof value === 'string' && value.startsWith('[ID:')) return value;
      if (typeof value === 'number') {
        return this.organizationMap[value] || `ID: ${value}`;
      }
      if (typeof value === 'string') return value;
      return String(value);
    },

    // === Подписи полей ===
    getFieldLabel(fieldName) {
      const labels = {
        'username': 'Логин',
        'first_name': 'Имя',
        'last_name': 'Фамилия',
        'email': 'Email',
        'phone': 'Телефон',
        'patronymic': 'Отчество',
        'is_active': 'Активен',
        'is_staff': 'Персонал',
        'organization': 'Организация',
        'local_org': 'Местная организация',
        'man': 'Номер билета',
        'reason': 'Причина',
        'region_org_old': 'Предыдущая организация',
        'region_org': 'Региональная организация',
        'date_disc': 'Дата прекращения',
        'name_mun': 'Муниципалитет',
        'protocol': 'Протокол',
        'status': 'Статус',
        'type_of': 'Тип',
        'date_start': 'Дата начала',
        'date_end': 'Дата окончания',
        'place': 'Место',
        'file': 'Файл',
        'year': 'Год',
        'term': 'Срок полномочий',
        'date_next': 'Дата следующего',
      };
      return labels[fieldName] || fieldName;
    },

    // === Ссылки на объекты ===
    getObjectUrl(modelName, objectId) {
      const modelToRoute = {
        'User': '/users',
        'FederalPublication': '/information/federal',
        'RegionPublication': '/information/region',
        'SimplePublication': '/information/simple',
        'PassportQuestionnaire': '/passport/organization',
        'HeadQuestionnaire': '/head',
        'AuditBody': '/passport',
        'GoverningBody': '/passport',
        'ManageBody': '/passport',
        'ParticipantChamber': '/passport',
        'ParticipantCouncil': '/passport',
        'Partner': '/passport/partner',
        'RealEstate': '/passport/immovables',
        'SupportForm': '/passport',
        'Moving': '/member/moving',
        'Refuse': '/member/refuse',
        'Continue': '/member/continue',
        'LocalOrganization': '/local-org',
        'PrimaryGroup': '/local-org/primary',
        'Justice': '/justice',
        'MeetingGoverningBody': '/meeting/governing',
        'SupremeBody': '/meeting/supreme',
        'ActiveAgreement': '/partner-agreement',
        'Person': '/school',
        'EventPlan': '/event',
        'BalanceSheet': '/balance-sheet',
      };

      const routePath = modelToRoute[modelName];
      if (routePath && objectId) {
        return `${routePath}/${objectId}`;
      }
      return null;
    }
  },

  watch: {}
};
</script>

<style scoped>
/* === Контейнер страницы === */
.user-history-page {
  padding: 16px 20px;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  background: #f5f7fa;
  min-height: 100vh;
}

.page-title {
  margin: 0 0 16px 0;
  color: #303133;
  font-size: 20px;
  font-weight: 600;
}

/* === Секция фильтров === */
.filters-section {
  margin-bottom: 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: #f5f7fa;
  padding: 8px 0;
}

.filter-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background-color: #fff;
  color: #303133;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.filter-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
  font-size: 14px;
  background: #fafafa;
}

/* === Кнопка экспорта === */
.button--export {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #67c23a;
  border-color: #67c23a;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

.button--export:hover {
  background: #85ce61;
  border-color: #85ce61;
}

.button--export:disabled {
  background: #dcdfe6;
  border-color: #dcdfe6;
  color: #909399;
  cursor: not-allowed;
}

.export-icon {
  flex-shrink: 0;
}

/* === Контейнер фильтров === */
.filters {
  padding: 12px 16px;
  background-color: #fafafa;
}

.filters--between {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  justify-content: flex-start;
}

.filters__block {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
  flex: 1 1 auto;
  min-width: 0;
}

.filters__block--wide {
  flex: 1 1 300px;
  max-width: 500px;
}

.filters__block--actions {
  flex: 0 0 auto;
  gap: 8px;
}

/* === Элементы фильтров === */
.filters__item {
  display: flex;
  flex-direction: column;
  min-width: 130px;
  max-width: 180px;
  flex: 0 1 auto;
}

.filters__item--wide {
  min-width: 200px;
  flex: 1 1 280px;
  max-width: 100%;
}

.filters__label {
  margin-bottom: 4px;
  font-size: 12px;
  color: #606266;
  font-weight: 500;
  white-space: nowrap;
}

/* === Select элементы === */
.select {
  font-size: 13px;
  min-height: 36px;
  height: auto;
  padding: 4px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  width: 100%;
}

.select:focus {
  border-color: #409eff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

/* === Кнопки === */
.button {
  padding: 8px 16px;
  font-size: 13px;
  min-height: 36px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  background: #409eff;
  border: 1px solid #409eff;
  color: #fff;
}

.button:hover {
  background: #66b1ff;
  border-color: #66b1ff;
}

.button--border {
  background: #fff;
  border: 1px solid #dcdfe6;
  color: #606266;
}

.button--border:hover {
  border-color: #409eff;
  color: #409eff;
}

/* === Таблица === */
.table-section {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow-x: auto;
}

.table-default--dynamic {
  width: 100%;
  min-width: 900px;
}

/* === Изменения в ячейке === */
.changes-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
}

.changes-list li {
  margin-bottom: 4px;
  word-break: break-word;
}

.old-value {
  text-decoration: line-through;
  color: #909399;
}

.new-value {
  font-weight: 500;
  color: #67c23a;
}

.no-changes {
  color: #909399;
  font-size: 12px;
  font-style: italic;
}

.action-link {
  color: #409eff;
  text-decoration: none;
  font-size: 13px;
}

.action-link:hover {
  text-decoration: underline;
}

/* === Пагинация === */
.pagination {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 16px;
  list-style: none;
  padding: 0;
}

.pagination__item {
  display: inline-block;
}

.pagination__link {
  display: block;
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  color: #606266;
  text-decoration: none;
  font-size: 13px;
  transition: all 0.2s;
}

.pagination__link:hover,
.pagination__item.active .pagination__link {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}

/* === Адаптивность === */
@media (max-width: 1200px) {
  .filters__item {
    min-width: 120px;
    max-width: 160px;
  }
}

@media (max-width: 992px) {
  .filters--between { gap: 8px; }
  .filters__block { width: 100%; }
  .filters__block--wide { max-width: 100%; }
  .filters__item { flex: 1 1 calc(50% - 6px); min-width: 120px; max-width: none; }
  .filters__item--wide { flex: 1 1 100%; }
  .filters__block--actions { width: 100%; justify-content: flex-end; }
}

@media (max-width: 576px) {
  .user-history-page { padding: 12px; }
  .filters__item { flex: 1 1 100%; min-width: 100%; max-width: 100%; }
  .filters__block { flex-direction: column; align-items: stretch; }
  .filters__block--actions { flex-direction: column; }
  .button { width: 100%; }
  .filter-card__header { flex-direction: column; gap: 10px; align-items: stretch; }
  .button--export { width: 100%; justify-content: center; }
}
</style>